import React from "react";

import { Box, Button, HStack, Select, SimpleGrid, Text, useTheme, VStack } from "@chakra-ui/react";
import { PhCalculationWithHistoryRequest, SupplementDosage } from "@henophess/types";

type TrendDirection = "up" | "down";

interface PhPattern {
  name: string;
  generateUrinePh: (generateTrending: boolean, trend: TrendDirection) => number[];
  generateSalivaPh: (generateTrending: boolean, trend: TrendDirection) => number[];
}

interface PhTestControlsProps {
  onTestDataGenerated: (data: PhCalculationWithHistoryRequest) => void;
  showTrendControls?: boolean;
}

const OPTIMAL_PH = 6.4;
const HIGH_PH_RANGE = { min: 6.8, max: 7.3 };
const LOW_PH_RANGE = { min: 5.2, max: 5.8 };
const FULL_PH_RANGE = { min: 5.2, max: 7.3 };

// Utility functions
const getRandomInRange = (min: number, max: number): number => {
  return Number((Math.random() * (max - min) + min).toFixed(1));
};

const generateTrendingValues = (n: number, startPh: number, trend: TrendDirection): number[] => {
  const values: number[] = [startPh];
  const minPh = Math.min(FULL_PH_RANGE.min, startPh);
  const maxPh = Math.max(FULL_PH_RANGE.max, startPh);

  for (let i = 0; i < n; i++) {
    const lastValue = values[values.length - 1];
    const nextValue =
      trend === "down"
        ? getRandomInRange(lastValue, maxPh) // For "up" trend, historical values should be higher
        : getRandomInRange(minPh, lastValue); // For "down" trend, historical values should be lower
    values.push(nextValue);
  }

  // For "up" trend: [current, week2 (higher), week1 (highest)]
  // For "down" trend: [current, week2 (lower), week1 (lowest)]
  return values;
};

const generateHighPh = (generateTrending: boolean, trend: TrendDirection): number[] => {
  const currentPh = getRandomInRange(HIGH_PH_RANGE.min, HIGH_PH_RANGE.max);

  if (!generateTrending) {
    return [currentPh];
  }

  return generateTrendingValues(2, currentPh, trend);
};

const generateLowPh = (generateTrending: boolean, trend: TrendDirection): number[] => {
  const currentPh = getRandomInRange(LOW_PH_RANGE.min, LOW_PH_RANGE.max);

  if (!generateTrending) {
    return [currentPh];
  }

  return generateTrendingValues(2, currentPh, trend);
};

const generateOptimalPh = (generateTrending: boolean, trend: TrendDirection): number[] => {
  const currentPh = OPTIMAL_PH;

  if (!generateTrending) {
    return [currentPh];
  }

  return generateTrendingValues(2, currentPh, trend);
};

const PH_PATTERNS: Record<string, PhPattern> = {
  allHigh: {
    name: "All High",
    generateUrinePh: generateHighPh,
    generateSalivaPh: generateHighPh
  },
  allLow: {
    name: "All Low",
    generateUrinePh: generateLowPh,
    generateSalivaPh: generateLowPh
  },
  highUrineLowSaliva: {
    name: "High Urine, Low Saliva",
    generateUrinePh: generateHighPh,
    generateSalivaPh: generateLowPh
  },
  lowUrineHighSaliva: {
    name: "Low Urine, High Saliva",
    generateUrinePh: generateLowPh,
    generateSalivaPh: generateHighPh
  },
  allOptimal: {
    name: "All Optimal",
    generateUrinePh: generateOptimalPh,
    generateSalivaPh: generateOptimalPh
  },
  optimalUrineLowSaliva: {
    name: "Optimal Urine, Low Saliva",
    generateUrinePh: generateOptimalPh,
    generateSalivaPh: generateLowPh
  },
  optimalUrineHighSaliva: {
    name: "Optimal Urine, High Saliva",
    generateUrinePh: generateOptimalPh,
    generateSalivaPh: generateHighPh
  },
  optimalSalivaLowUrine: {
    name: "Optimal Saliva, Low Urine",
    generateUrinePh: generateLowPh,
    generateSalivaPh: generateOptimalPh
  },
  optimalSalivaHighUrine: {
    name: "Optimal Saliva, High Urine",
    generateUrinePh: generateHighPh,
    generateSalivaPh: generateOptimalPh
  },
  random: {
    name: "Random",
    generateUrinePh: (generateTrending: boolean, trend: TrendDirection) => {
      const min = LOW_PH_RANGE.min;
      const max = HIGH_PH_RANGE.max;
      const currentPh = getRandomInRange(min, max);
      if (!generateTrending) {
        return [currentPh];
      }
      return generateTrendingValues(2, currentPh, trend);
    },
    generateSalivaPh: (generateTrending: boolean, trend: TrendDirection) => {
      const min = LOW_PH_RANGE.min;
      const max = HIGH_PH_RANGE.max;
      const currentPh = getRandomInRange(min, max);
      if (!generateTrending) {
        return [currentPh];
      }
      return generateTrendingValues(2, currentPh, trend);
    }
  }
};

export const PhTestControls: React.FC<PhTestControlsProps> = ({ onTestDataGenerated, showTrendControls = true }) => {
  const theme = useTheme();
  const [urineTrend, setUrineTrend] = React.useState<TrendDirection>("up");
  const [salivaTrend, setSalivaTrend] = React.useState<TrendDirection>("up");

  const generateTestData = (pattern: string) => {
    const selectedPattern = PH_PATTERNS[pattern];
    if (!selectedPattern) return;

    // Generate values based on whether we need trending data
    const urineValues = selectedPattern.generateUrinePh(showTrendControls, urineTrend);
    const salivaValues = selectedPattern.generateSalivaPh(showTrendControls, salivaTrend);

    const testData: PhCalculationWithHistoryRequest = {
      urinePh: urineValues[0], // Current pH is always first
      week2AverageUrinePh: urineValues[1] || urineValues[0], // Use current pH if no trending data
      week1AverageUrinePh: urineValues[2] || urineValues[0], // Use current pH if no trending data
      salivaPh: salivaValues[0], // Current pH is always first
      week2AverageSalivaPh: salivaValues[1] || salivaValues[0], // Use current pH if no trending data
      week1AverageSalivaPh: salivaValues[2] || salivaValues[0], // Use current pH if no trending data
      supplementRecommendations: [] as SupplementDosage[]
    };

    onTestDataGenerated(testData);
  };

  const mainPatterns = Object.entries(PH_PATTERNS).filter(([key]) => key !== "random");

  return (
    <Box sx={theme.components.PhTestControls.baseStyle}>
      <Text fontSize="md" fontWeight="medium" mb={3} textAlign="center" color="brand.dark_green">
        Test Mode
      </Text>
      <HStack spacing={6} align="flex-start">
        <VStack spacing={2} flex={1}>
          <SimpleGrid columns={3} spacing={1} width="full">
            {mainPatterns.map(([key, pattern]) => (
              <Button
                key={key}
                size="xs"
                onClick={() => generateTestData(key)}
                fontSize="xs"
                bg="brand.sage_green"
                color="white"
                _hover={{ bg: "brand.dark_green" }}
              >
                {pattern.name}
              </Button>
            ))}
          </SimpleGrid>
          <Button
            size="xs"
            onClick={() => generateTestData("random")}
            fontSize="xs"
            bgGradient="linear(to-r, red.500, yellow.500, green.500, blue.500, purple.500)"
            color="white"
            _hover={{
              bgGradient: "linear(to-r, red.600, yellow.600, green.600, blue.600, purple.600)"
            }}
            width="150px"
          >
            Random
          </Button>
        </VStack>
        {showTrendControls && (
          <VStack spacing={2} align="stretch" minW="150px">
            <Box className="trend-control-box">
              <Text fontSize="xs">Saliva Trend</Text>
              <Select size="xs" value={salivaTrend} onChange={(e) => setSalivaTrend(e.target.value as TrendDirection)}>
                <option value="up">Trending Up</option>
                <option value="down">Trending Down</option>
              </Select>
            </Box>
            <Box className="trend-control-box">
              <Text fontSize="xs">Urine Trend</Text>
              <Select size="xs" value={urineTrend} onChange={(e) => setUrineTrend(e.target.value as TrendDirection)}>
                <option value="up">Trending Up</option>
                <option value="down">Trending Down</option>
              </Select>
            </Box>
          </VStack>
        )}
      </HStack>
    </Box>
  );
};
